<script setup>
  /**
   * Shown when ServerHealthService proves our backend is down while the user's
   * connection is fine. Deliberately kept apart from BcsConnectionStatus: that
   * component owns client connectivity and must keep behaving exactly as it does.
   *
   * There is no polling here. The modal only opens on a state transition reported
   * by ServerHealthService, and closes as soon as any API call succeeds.
   */
  import { ref, onMounted, onUnmounted, nextTick } from 'vue'
  import serverHealthService from '../../services/ServerHealthService'
  import BcsModal from '../BcsModal.vue'
  import { SERVER_TEXT_DEFINITIONS } from './constants'

  const props = defineProps({
    serverUnavailableText: {
      type: String,
      default: SERVER_TEXT_DEFINITIONS.serverUnavailableMessage.body
    },
    serverUnavailableTitle: {
      type: String,
      default: SERVER_TEXT_DEFINITIONS.serverUnavailableMessage.title
    },
    reloadButtonText: {
      type: String,
      default: 'Reload'
    }
  })

  const modalRef = ref(null)

  const handleServerStatusChange = (degraded) => {
    if (!degraded) {
      modalRef.value?.closeModal()
      return
    }

    nextTick(() => {
      modalRef.value?.openModal()
    })
  }

  // A probe answering does not prove the backend is healthy again, so the only
  // action offered is a reload; a real recovery closes the modal on its own.
  const handleModalButtonClick = () => {
    window.location.reload()
  }

  onMounted(() => {
    serverHealthService.registerListener(handleServerStatusChange)
  })

  onUnmounted(() => {
    serverHealthService.unregisterListener(handleServerStatusChange)
  })
</script>

<template>
  <BcsModal
    @modalButtonClick="handleModalButtonClick"
    ref="modalRef"
    :elevated="true"
    :disableOutsideClick="true"
    :hideCloseButton="true"
    :modalSize="'small'"
    :modalType="'warning'"
    :modalButton="props.reloadButtonText"
    :modalTitle="props.serverUnavailableTitle">
    <div class="server-message">
      {{ props.serverUnavailableText }}
    </div>
  </BcsModal>
</template>

<style scoped>
.server-message {
  text-align: center;
  margin-bottom: 20px;
}
</style>
